import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useSettings } from '../../features/settings/shared';
import { qrMatrix } from '../../features/musicEdition/profileQr';
import { contentAppUrl } from '../../utils/openInApp';

// Tablets use the QR dialog too, including iPad's desktop user agent.
export function isPreviewPhone(nav = navigator) {
  if (/iPad/i.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1))
    return false;
  return /iPhone|iPod/i.test(nav.userAgent) || /Android.*Mobile/i.test(nav.userAgent);
}

export function feedPreviewUrl(identifier, origin = window.location.origin) {
  // Carry the content address only; never copy auth tokens or current query parameters.
  return `${origin}/feed/${encodeURIComponent(identifier)}?previewInApp=1`;
}

function PreviewQr({ url, label }) {
  const matrix = useMemo(() => qrMatrix(url), [url]);
  const size = matrix.length + 8; // Four-module quiet zone on every edge.
  const path = matrix
    .flatMap((row, y) => row.map((on, x) => (on ? `M${x + 4} ${y + 4}h1v1h-1z` : '')))
    .join('');
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${size} ${size}`}
        width="240"
        height="240"
        style={{ maxWidth: '100%', height: 'auto', background: '#fff' }}
        shapeRendering="crispEdges"
      >
        <rect width={size} height={size} fill="#fff" />
        <path d={path} fill="#000" />
      </svg>
    </Box>
  );
}

export function FeedAppPreviewLink({ identifier, landing = false }) {
  const { en } = useSettings();
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const appUrl = contentAppUrl(`/feed/${encodeURIComponent(identifier)}`);
  const url = feedPreviewUrl(identifier);
  const Action = landing ? Button : 'a';
  return (
    <>
      <Action
        {...(landing ? { variant: 'contained', sx: { my: 2, minHeight: 48 } } : {})}
        href={appUrl}
        onClick={(event) => {
          if (!isPreviewPhone()) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {landing
          ? en
            ? 'Open this feed in the app'
            : '앱에서 이 피드 열기'
          : en
            ? 'Preview in app (admin login required)'
            : '앱에서 미리보기 (관리자 로그인 필요)'}
      </Action>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        className="pp-review-dialog"
        aria-labelledby={titleId}
      >
        <DialogTitle id={titleId}>
          {en ? 'Preview on your phone' : '휴대폰에서 피드 미리보기'}
        </DialogTitle>
        <DialogContent>
          <p>
            {en
              ? 'Scan with your phone camera to open this feed in Postpop. If the app does not open automatically, tap “Open this feed in the app” on the page.'
              : '휴대폰 카메라로 QR을 스캔하면 이 피드로 연결됩니다. 앱이 자동으로 열리지 않으면 연결된 화면에서 ‘앱에서 이 피드 열기’를 눌러주세요.'}
          </p>
          <PreviewQr url={url} label={en ? 'Feed preview QR code' : '피드 미리보기 QR 코드'} />
          <p>
            {en
              ? 'Sign in as an administrator in the app. Scanning this code does not grant access to hidden feeds.'
              : '앱에도 관리자 계정으로 로그인해 주세요. QR만으로 숨긴 피드의 접근 권한이 생기지는 않습니다.'}
          </p>
          <Button component="a" href={appUrl} fullWidth variant="outlined">
            {en ? 'Open the app on this device' : '이 기기에서 앱 열기'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{en ? 'Close' : '닫기'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// QR fallback must be reachable without a browser login. It reveals no feed data;
// the app (or the normal web detail route) still checks the current account.
export function FeedPreviewHandoff({ identifier }) {
  const { en, style } = useSettings();
  const attempted = useRef(null);
  useEffect(() => {
    if (!isPreviewPhone() || attempted.current === identifier) return;
    attempted.current = identifier;
    const url = contentAppUrl(`/feed/${encodeURIComponent(identifier)}`);
    if (url) window.location.assign(url);
  }, [identifier]);
  return (
    <PageLayout>
      <Helmet>
        <title>{en ? 'Feed preview · Postpop' : '피드 미리보기 · 포스트팝'}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <section className="pp-settings pp-article-detail" style={style}>
        <div className="pp-article-state">
          <h1>{en ? 'Preview in Postpop' : '포스트팝 앱에서 미리보기'}</h1>
          <p>
            {en
              ? 'Open this feed in the Postpop app. Hidden feeds require an administrator account in the app.'
              : '아래 버튼으로 이 피드를 열어주세요. 숨긴 피드는 앱에서도 관리자 계정으로 로그인해야 볼 수 있습니다.'}
          </p>
          <FeedAppPreviewLink identifier={identifier} landing />
          <p>
            <Link to={`/feed/${encodeURIComponent(identifier)}`}>
              {en ? 'View on the web' : '웹에서 확인'}
            </Link>
          </p>
        </div>
      </section>
    </PageLayout>
  );
}
